import React from "react";
import { makeObservable, observable } from "mobx";
import { observer } from "mobx-react";
import { ImgCost } from "./Images/ImgCost.tsx";
import { formatTokenCount, formatUsd, usdTitle, usdRate } from "../utils/format.ts";
import type { Message } from "../models.ts";
import { pricingService } from "../services/pricingService.ts";

export interface MessageCostProps {
  message: Message;
}

interface CostRow {
  label: string;
  value: string;
  /** Hover hint on the token count — the per-1M rate, e.g. "$2.00 / 1M tokens". */
  title?: string;
  highlight?: boolean;
}

interface PopupPos {
  left: number;
  top: number;
  dir: 'up' | 'down';
}

/**
 * Estimated USD cost badge for a chat turn.
 *
 * The turn cost is the sum of:
 *  - the assistant LLM cost (message.costUsd, already summed across all rounds of a
 *    collapsed message by chatExecutionService), plus
 *  - the cost of every generated image/video attachment (attachmentMetas[].costUsd).
 *
 * The badge shows the total in USD; clicking it opens a breakdown popup (tokens,
 * megapixels, video seconds). Hidden when nothing has a cost (pricing disabled,
 * or a pre-existing message that predates the feature).
 */
@observer
export class MessageCost extends React.Component<MessageCostProps> {
  @observable private _showPopup: boolean = false;
  @observable private _popupHiding: boolean = false;
  @observable private _pos: PopupPos | null = null;
  private _badgeRef = React.createRef<HTMLSpanElement>();
  private _hideTimer: ReturnType<typeof setTimeout> | null = null;

  constructor(props: MessageCostProps) {
    super(props);
    makeObservable(this);
  }

  componentDidMount() {
    pricingService.load();
    document.addEventListener('click', this._onDocumentClick);
    window.addEventListener('scroll', this._onScroll, true);
    window.addEventListener('resize', this._onScroll);
  }

  componentWillUnmount(): void {
    document.removeEventListener('click', this._onDocumentClick);
    window.removeEventListener('scroll', this._onScroll, true);
    window.removeEventListener('resize', this._onScroll);
    if (this._hideTimer) clearTimeout(this._hideTimer);
  }

  private _hidePopup = () => {
    if (!this._showPopup) return;
    if (this._hideTimer) clearTimeout(this._hideTimer);
    this._popupHiding = true;
    // Let the tc-popup-out animation finish before unmounting.
    this._hideTimer = setTimeout(() => {
      this._showPopup = false;
      this._popupHiding = false;
      this._pos = null;
      this._hideTimer = null;
    }, 150);
  }

  // Hide on any scroll (the fixed popup would otherwise drift from the badge).
  private _onScroll = () => this._hidePopup();

  private _onDocumentClick = (e: MouseEvent) => {
    if (this._badgeRef.current && (this._badgeRef.current as Node).contains(e.target as Node)) return;
    this._hidePopup();
  }

  private _onToggle = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (this._showPopup) {
      this._hidePopup();
      return;
    }
    const el = this._badgeRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const width = 260; // approx popup width, used to clamp within the viewport
    const left = Math.max(8, Math.min(rect.left, window.innerWidth - width - 8));
    const dir: 'up' | 'down' = rect.top >= 240 ? 'up' : 'down';
    const top = dir === 'up' ? rect.top - 8 : rect.bottom + 8;
    if (this._hideTimer) { clearTimeout(this._hideTimer); this._hideTimer = null; }
    this._popupHiding = false;
    this._pos = { left, top, dir };
    this._showPopup = true;
  }

  render() {
    const { message } = this.props;

    const llmCost = message.costUsd || 0;
    const metas = message.attachmentMetas || [];
    const attachmentCost = metas.reduce((s, m) => s + (m.costUsd || 0), 0);
    const total = llmCost + attachmentCost;

    // Nothing to show — pricing disabled or pre-existing message
    if (total <= 0) return null;

    const rows: CostRow[] = [];

    // LLM breakdown. For tool messages the cost comes from the tool's own model calls
    // (sub-agent / OCR) rather than the main assistant turn, so label it accordingly.
    const costLabel = message.role === 'tool'
      ? (message.toolName === 'run_agent' || message.toolName === 'run_skill' ? 'Sub-agent'
        : message.toolName === 'ocr_image' ? 'OCR' : 'LLM')
      : 'LLM';
    if (llmCost > 0) {
      const tokensIn = message.tokensIn || 0;
      const tokensInCached = Math.min(message.tokensInCached || 0, tokensIn);
      const tokensOut = message.tokensOut || 0;
      const fresh = Math.max(0, tokensIn - tokensInCached);
      const rates = message.modelName ? pricingService.getLlmRates(message.modelName) : null;
      const rateHint = (n?: number): string | undefined =>
        n != null ? usdRate(n) : undefined;
      rows.push({
        label: costLabel,
        value: formatUsd(llmCost),
        title: usdTitle(llmCost),
        highlight: true,
      });
      if (fresh > 0) {
        rows.push({ label: 'Input (fresh)', value: formatTokenCount(fresh), title: rateHint(rates?.input) });
      }
      if (tokensInCached > 0) {
        rows.push({ label: 'Input (cached)', value: formatTokenCount(tokensInCached), title: rateHint(rates?.cacheRead) });
      }
      if (tokensOut > 0) {
        rows.push({ label: 'Output', value: formatTokenCount(tokensOut), title: rateHint(rates?.output) });
      }
    }

    // Image / video breakdown
    for (const m of metas) {
      if (!m.costUsd || m.costUsd <= 0) continue;
      const isVideo = m.contentType.startsWith('video/');
      if (isVideo) {
        const seconds = m.videoFps ? (m.videoFrames || 0) / m.videoFps : 0;
        const res = m.videoHeight ? `${m.videoWidth}×${m.videoHeight}` : '';
        rows.push({
          label: `Video${res ? ` (${res})` : ''}`,
          value: `${seconds.toFixed(1)}s · ${formatUsd(m.costUsd)}`,
          title: usdTitle(m.costUsd),
        });
      } else {
        const mp = (m.imageWidth && m.imageHeight) ? (m.imageWidth * m.imageHeight) / 1_000_000 : 0;
        rows.push({
          label: `Image${mp ? ` (${mp.toFixed(2)} MP)` : ''}`,
          value: formatUsd(m.costUsd),
          title: usdTitle(m.costUsd),
        });
      }
    }

    const pos = this._pos;
    const popupStyle: React.CSSProperties | undefined = pos
      ? {
          left: pos.left,
          top: pos.top,
          transform: pos.dir === 'up' ? 'translateY(-100%)' : 'none',
        }
      : undefined;

    return (
      <span ref={this._badgeRef} className="message-cost message-cost--clickable" onClick={this._onToggle} title={usdTitle(total)}>
        <ImgCost size={12} /> {formatUsd(total)}
        {(this._showPopup || this._popupHiding) && pos && (
          <div
            className={`tc-popup mc-popup${this._popupHiding ? ' tc-popup--hiding' : ''}`}
            style={popupStyle}
            onClick={e => e.stopPropagation()}
          >
            <div className="tc-popup-title">Estimated cost</div>
            {rows.map((row, i) => (
              <div className="tc-popup-row" key={i}>
                <span className="tc-popup-label">{row.label}</span>
                <span className={`tc-popup-value${row.highlight ? ' tt-value-highlight' : ''}`} title={row.title}>{row.value}</span>
              </div>
            ))}
            <div className="tc-popup-row mc-popup-total">
              <span className="tc-popup-label">Total</span>
              <span className="tc-popup-value tt-value-highlight" title={usdTitle(total)}>{formatUsd(total)}</span>
            </div>
          </div>
        )}
      </span>
    );
  }
}
